import { KeyRound } from "lucide-react";

import { cn } from "@/shared/lib/utils";

export interface PrincipalAvatarProps {
  /** Display name — used for initials and the deterministic hue (persons). */
  name: string;
  /** A person renders an initials circle; an API key renders a key glyph. */
  kind?: "person" | "key";
  size?: number;
  className?: string;
}

/**
 * Avatar for an access principal. Persons get a colored initials circle whose hue is derived
 * deterministically from the name; API keys get a neutral square with a key glyph. The hue/size are
 * data-driven, so they ride inline styles rather than utility classes.
 */
export function PrincipalAvatar({ name, kind = "person", size = 32, className }: PrincipalAvatarProps) {
  if (kind === "key") {
    return (
      <div
        className={cn(
          "grid shrink-0 place-items-center rounded-md border border-border bg-muted text-muted-foreground",
          className,
        )}
        style={{ width: size, height: size }}
      >
        <KeyRound style={{ width: size * 0.5, height: size * 0.5 }} />
      </div>
    );
  }

  const initials = name
    .split(" ")
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const hue = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0) % 360;

  return (
    <div
      className={cn("grid shrink-0 place-items-center rounded-full font-mono font-bold", className)}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.34,
        background: `oklch(0.80 0.07 ${hue})`,
        color: `oklch(0.30 0.08 ${hue})`,
      }}
    >
      {initials}
    </div>
  );
}
